import { createFileRoute, notFound, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { runPortalReport } from "@/lib/portal-reports/runtime.functions";
import { runServerFn } from "@/lib/server-fn";
import { getReport } from "@/lib/reporting/framework/registry";
import { ReportShell } from "@/components/portal/report-shell";
import type { ReportFilters } from "@/lib/reporting/framework/types";

export const Route = createFileRoute("/_authenticated/portal/reports/$reportKey")({
  component: PortalReportView,
  errorComponent: ({ error }) => (
    <div className="p-6"><div className="text-sm text-destructive">Couldn't load report: {error.message}</div></div>
  ),
  notFoundComponent: () => <div className="p-6 text-sm text-muted-foreground">Report not found.</div>,
});

function PortalReportView() {
  const { reportKey } = Route.useParams();
  const navigate = useNavigate();
  const def = getReport(reportKey);
  if (!def) throw notFound();

  const [filters, setFilters] = useState<ReportFilters>(() => {
    const to = new Date();
    const from = new Date(to.getTime() - 30 * 86_400_000);
    return { dateFrom: from.toISOString(), dateTo: to.toISOString() };
  });

  const runFn = useServerFn(runPortalReport);
  const { data, isFetching, error } = useQuery({
    queryKey: ["portal-report", reportKey, filters],
    queryFn: () => runServerFn(() => runFn({ data: { key: reportKey, filters } }), "Running report"),
    retry: false,
  });

  const errMsg = error instanceof Error ? error.message : null;
  const isAccessDenied = errMsg?.includes("not enabled for your account");

  if (isAccessDenied) {
    return (
      <div>
        <div className="px-4 sm:px-6 pt-4">
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/portal/reports" })}>
            <ArrowLeft className="h-4 w-4 mr-1" /> All reports
          </Button>
        </div>
        <div className="p-6">
          <div className="border border-dashed rounded-md py-12 px-6 text-center max-w-lg mx-auto space-y-3">
            <div className="mx-auto h-10 w-10 rounded-full bg-muted flex items-center justify-center">
              <Lock className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="text-sm font-medium">{def.title} isn't enabled for your account</div>
            <div className="text-xs text-muted-foreground">
              Contact your account manager if you need access to this report.
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate({ to: "/portal/reports" })}>
              Back to reports
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="px-4 sm:px-6 pt-4">
        <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/portal/reports" })}>
          <ArrowLeft className="h-4 w-4 mr-1" /> All reports
        </Button>
      </div>
      <ReportShell
        definition={def}
        filters={filters}
        onFiltersChange={setFilters}
        loading={isFetching}
        result={data as any}
      />
    </div>
  );
}